import { useCallback, useEffect, useMemo, useRef } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown/index";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useCouponBomListAPICoupon } from "@/app/hooks/api_hooks/coupon/useCouponBomListAPICoupon";
import { useCouponSubCategoryAPICoupon } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPICoupon";
import { useCouponSubServiceListAPICoupon } from "@/app/hooks/api_hooks/coupon/useCouponSubServiceListAPICoupon";
import { FULL_WIDTH_DROPDOWN, InlineField, SubPanel } from "./components";
import { ALL, IScopeForm, pickItems } from "./interface";

type Field = keyof IScopeForm;
type SelectMeta = { selectAllUsed?: boolean };

interface IItemScopeSectionProps {
  type: "SERVICE" | "BOM";
  value: IScopeForm;
  onChange: (value: IScopeForm) => void;
  categoryItems: ISelectMenuItemData[];
  showExclude: boolean;
  disabled?: boolean;
}

const isAll = (ids: string[]) => ids.includes(ALL);
// Options shown as selected: everything for "all", else the picked ids.
const selectedOf = (ids: string[], options: ISelectMenuItemData[]) => (isAll(ids) ? options : pickItems(options, ids));
// Exclude options stay inside what is included.
const within = (ids: string[], options: ISelectMenuItemData[]) =>
  isAll(ids) ? options : options.filter((o) => ids.includes(o.id));
const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));
// The paged dropdown registers a hidden input; keep it out of the form so it never blocks Save.
const noRegister = (name: string) => ({
  name,
  onChange: async () => undefined,
  onBlur: async () => undefined,
  ref: () => undefined,
});

// Item lists can run into thousands: the coupon screen's paged dropdown (100 rows at a time).
const LongList = ({
  name,
  options,
  selectedIds,
  onSelect,
  disabled,
}: {
  name: string;
  options: ISelectMenuItemData[];
  selectedIds: string[];
  onSelect: (ids: string[], meta?: SelectMeta) => void;
  disabled: boolean;
}) => {
  // The dropdown re-sends its selection whenever this callback changes identity, so keep it stable.
  const latest = useRef(onSelect);
  latest.current = onSelect;
  const handle = useCallback((ids: string[], meta?: SelectMeta) => latest.current(ids ?? [], meta), []);
  if (!options.length) {
    return (
      <MultiSelectDropdown
        data={[]}
        selectedKey="id"
        onSelectItem={NO_OP}
        register={noRegister}
        name={name}
        disabled
        containerClassName={FULL_WIDTH_DROPDOWN}
      />
    );
  }
  return (
    <MultiSelectDropdown
      // Remount on a new list so the saved picks are applied to it.
      key={`${options.length}:${options[0].id}`}
      data={options}
      selectedKey="id"
      onSelectItem={handle}
      defaultSelected={selectedIds}
      register={noRegister}
      name={name}
      disabled={disabled}
      containerClassName={FULL_WIDTH_DROPDOWN}
    />
  );
};
const NO_OP = () => undefined;

// Same three dropdowns as the coupon setup: category → sub category → items, plus matching excludes.
const ItemScopeSection = ({
  type,
  value,
  onChange,
  categoryItems,
  showExclude,
  disabled = false,
}: IItemScopeSectionProps) => {
  const { execute: fetchSubCategories, serviceSubCategory } = useCouponSubCategoryAPICoupon();
  const { execute: fetchSubServices, subServiceList } = useCouponSubServiceListAPICoupon();
  const { execute: fetchBoms, bomlist } = useCouponBomListAPICoupon();
  const label = type === "SERVICE" ? "Sub Services" : "BOMs";

  const categoryKey = value.category.join("|");
  const subCategoryKey = value.sub_category.join("|");
  useEffect(() => {
    if (value.category.length) fetchSubCategories(value.category);
  }, [categoryKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!value.category.length || !value.sub_category.length) return;
    // The listings take ids or ["all_inclusive"] (no filter), like the coupon setup.
    const fetchItems = type === "SERVICE" ? fetchSubServices : fetchBoms;
    fetchItems(value.sub_category as never, value.category as never);
  }, [categoryKey, subCategoryKey, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const subCategoryItems = useMemo<ISelectMenuItemData[]>(
    () =>
      value.category.length
        ? serviceSubCategory.map((s) => ({ id: String(s.subcategory_id), title: s.subcategory_name }))
        : [],
    [serviceSubCategory, value.category.length],
  );
  const itemOptions = useMemo<ISelectMenuItemData[]>(() => {
    if (!value.category.length || !value.sub_category.length) return [];
    return type === "SERVICE"
      ? subServiceList.map((s) => ({ id: String(s.sub_service_id), title: s.sub_service_name }))
      : bomlist.map((b) => ({ id: String(b.bom_id), title: b.bom_name }));
  }, [type, subServiceList, bomlist, value.category.length, value.sub_category.length]);

  const excludeOptions = useMemo(
    () => ({
      ex_category: within(value.category, categoryItems),
      ex_sub_category: within(value.sub_category, subCategoryItems),
      ex_items: within(value.items, itemOptions),
    }),
    [value.category, value.sub_category, value.items, categoryItems, subCategoryItems, itemOptions],
  );

  // Ids each dropdown shows as picked ("all" = every option); also stops the menu clearing them on edit.
  const shownIds = useMemo(() => {
    const ids = (field: Field, options: ISelectMenuItemData[]) =>
      isAll(value[field]) ? options.map((o) => o.id) : value[field];
    return {
      category: ids("category", categoryItems),
      sub_category: ids("sub_category", subCategoryItems),
      items: ids("items", itemOptions),
      ex_category: value.ex_category,
      ex_sub_category: value.ex_sub_category,
      ex_items: value.ex_items,
    };
  }, [value, categoryItems, subCategoryItems, itemOptions]);

  // A parent change resets picked children ("all" stays all); everything selected is saved as "all".
  const setInclude = (
    field: "category" | "sub_category" | "items",
    options: ISelectMenuItemData[],
    ids: string[],
    meta?: SelectMeta,
  ) => {
    const picked = meta?.selectAllUsed || (options.length > 0 && ids.length === options.length) ? [ALL] : ids;
    if (sameIds(picked, value[field])) return;
    const next = { ...value, [field]: picked };
    const children: Field[] =
      field === "category" ? ["sub_category", "items"] : field === "sub_category" ? ["items"] : [];
    children.forEach((child) => {
      if (!isAll(next[child])) next[child] = [];
    });
    onChange(next);
  };
  const onInclude =
    (field: "category" | "sub_category", options: ISelectMenuItemData[]) =>
    (items: ISelectMenuItemData[], meta?: SelectMeta) =>
      setInclude(
        field,
        options,
        (items ?? []).map((i) => String(i.id)),
        meta,
      );
  const setExclude = (field: "ex_category" | "ex_sub_category" | "ex_items", ids: string[]) => {
    if (!sameIds(ids, value[field])) onChange({ ...value, [field]: ids });
  };
  const onExclude = (field: "ex_category" | "ex_sub_category") => (items: ISelectMenuItemData[]) =>
    setExclude(
      field,
      (items ?? []).map((i) => String(i.id)),
    );

  const dropdown = (
    field: Field,
    options: ISelectMenuItemData[],
    placeholder: string,
    onSelect: (items: ISelectMenuItemData[], meta?: SelectMeta) => void,
    withSelectAll = true,
  ) => (
    <SearchableMultiSelectMenu
      onSelectItem={onSelect}
      errorMessages={[]}
      fieldError={undefined}
      register={{ name: `surge-${type}-${field}` } as UseFormRegisterReturn}
      withSelectAll={withSelectAll}
      showTooltips={true}
      showTypedErrors={false}
      showDropdownIcon={true}
      placeholder={placeholder}
      defaultSelected={selectedOf(value[field], options)}
      defaultSelectedIds={shownIds[field]}
      selectItems={options}
      disabled={disabled || !options.length}
      listBoxClassName="w-full"
      className="text-gray-800"
      containerClassName="w-full"
    />
  );

  return (
    <SubPanel>
      <p className="text-xs font-semibold text-gray-700">{type === "SERVICE" ? "Services" : "BOMs"}</p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InlineField label="Category" required>
          {dropdown("category", categoryItems, "Select Category", onInclude("category", categoryItems))}
        </InlineField>
        <InlineField label="Sub Category" required>
          {dropdown(
            "sub_category",
            subCategoryItems,
            "Select Sub Category",
            onInclude("sub_category", subCategoryItems),
          )}
        </InlineField>
        <InlineField label={label} required>
          <LongList
            name={`surge-${type}-items`}
            options={itemOptions}
            selectedIds={shownIds.items}
            onSelect={(ids, meta) => setInclude("items", itemOptions, ids, meta)}
            disabled={disabled}
          />
        </InlineField>
      </div>
      {showExclude && (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <InlineField label="Exclude Category">
            {dropdown("ex_category", excludeOptions.ex_category, "None", onExclude("ex_category"), false)}
          </InlineField>
          <InlineField label="Exclude Sub Category">
            {dropdown("ex_sub_category", excludeOptions.ex_sub_category, "None", onExclude("ex_sub_category"), false)}
          </InlineField>
          <InlineField label={`Exclude ${label}`}>
            <LongList
              name={`surge-${type}-ex-items`}
              options={excludeOptions.ex_items}
              selectedIds={shownIds.ex_items}
              onSelect={(ids) => setExclude("ex_items", ids)}
              disabled={disabled}
            />
          </InlineField>
        </div>
      )}
    </SubPanel>
  );
};

export default ItemScopeSection;
